import { useState, useEffect, useMemo, ChangeEvent } from 'react';
import {
	LocationPin,
	LocationWhite,
	Map,
	MagnifyingGlassGold,
	GoogleName
} from '@/resources/assets/images';
import { FilterQueryType } from '@/modules/dashboard/dashboardTypes';
import { sendAnalytics } from '@/services/analyticsService';
import {
	stateMapping,
	countryMapping
} from '@/components/HeaderRefactored/stateCountryMapping';
import Button from '@/components/Button/newButtons';
import { Popover } from '@/components/atoms/Popover';
import { Input } from '@/components/atoms/Input';
import Loader from '@/components/Loader/Loader';
import { debounce } from 'lodash';
import type { GetGooglePlacesResponse } from '@/services/dashboardService';
import { useUserData } from '@/modules/lists/details/src/hooks/useUserData';
import { RangeSlider } from '@/components/atoms/RangeSlider';
import { Tooltip } from '@/components/atoms/Tooltip';
import { FilterComponents, useFilters } from '../../molecules/FilterComponents';
import { useMutateLocationSuggestions } from './src/hooks/useMutateLocationSuggestions';
import { useFiltersContext } from '../../../providers/FilterProvider';
import type { FilterProps } from '../../../@types';

type FilterLocationProps = FilterProps & {
	handleSwitchView?: (view: 'businesses' | 'map') => void;
};

const getValidMapping = (input: string, mapping: Record<string, string>) =>
	mapping[input] ||
	(Object.values(mapping).includes(input) ? input : undefined);

export const FilterLocation = ({
	appliedFilters,
	handleSwitchView,
	onFilterChange
}: FilterLocationProps) => {
	const { user } = useUserData();

	const { selectedLocation, handleChangeLocation, handleClearLocation } =
		useFiltersContext();

	const isSliderDisabled = Boolean(
		appliedFilters?.state || appliedFilters?.country
	);

	const handleClearLocationFilters = () => {
		const newQuery = {
			...appliedFilters,
			coordinates: undefined,
			state: undefined,
			country: undefined,
			maxDistance: 40,
			minDistance: 0,
			selectedLocation: undefined,
			sort: undefined
		};

		sendAnalytics({
			type: 'track',
			eventName: 'User cleared location filter',
			properties: { selectedLocation }
		});

		onFilterChange(newQuery);

		handleClearLocation();

		handleSwitchView?.('businesses');
	};

	const handleRadiusChange = (val: [number, number]) => {
		const [minDistance = 0, maxDistance = 40] = val;

		const newQuery = {
			...appliedFilters,
			minDistance,
			maxDistance,
			count: true,
			limit: 50,
			skip: 0,
			queryType: FilterQueryType.FILTER
		};

		onFilterChange(newQuery);

		sendAnalytics({
			type: 'track',
			eventName: 'User changed filters',
			properties: { filterChanged: 'Radius', appliedFilters: newQuery }
		});
	};

	const handleShowOnMap = () => {
		sendAnalytics({
			type: 'track',
			eventName: 'User pressed show on map in location tab',
			properties: { selectedLocation }
		});

		handleSwitchView?.('map');
	};

	const handlePlaceSelect = ({
		geometry: {
			location: { lat, lng }
		},
		formatted_address
	}: Pick<GetGooglePlacesResponse, 'formatted_address' | 'geometry'>) => {
		handleChangeLocation(formatted_address);

		const parts = formatted_address.split(',').map(part => part.trim());

		const [stateInput, countryInput] = parts;

		const state =
			parts.length === 2
				? getValidMapping(stateInput, stateMapping)
				: null;
		const country = getValidMapping(
			parts.length === 2 ? countryInput : parts[0],
			countryMapping
		);

		const { features: { businessSortingTestVariant } = {} } = user;

		const hasSortingFeature = businessSortingTestVariant !== null;
		const isNotControlVariant = businessSortingTestVariant !== 'control';
		const missingSortValue = !appliedFilters?.sort;
		const missingListIdValue = !appliedFilters?.listId;

		const useScoreSorting =
			hasSortingFeature &&
			isNotControlVariant &&
			missingSortValue &&
			missingListIdValue;

		onFilterChange({
			...appliedFilters,
			state,
			country,
			coordinates: `[${lng}, ${lat}]`,
			sortField: useScoreSorting ? 'score' : 'distance',
			sort: useScoreSorting ? { score: '-1' } : { distance: '1' }
		});
	};

	return (
		<FilterComponents.Wrapper moduleName="location">
			<FilterComponents.Header
				showClearFiltersButton={selectedLocation !== undefined}
				icon={LocationPin}
				title="Location"
				activeFiltersCount={selectedLocation ? 1 : 0}
				onClearFilters={handleClearLocationFilters}
			/>

			<FilterComponents.Content>
				{selectedLocation && (
					<section className="inline-flex h-6 w-full items-center gap-1 rounded-full border border-dm-gold bg-dm-gold px-2 text-sm text-dm-black1">
						<LocationWhite className="h-4 w-4 [&_path]:stroke-dm-black1" />

						<span className="max-w-44 truncate">
							{selectedLocation}
						</span>
					</section>
				)}

				<p className="text-sm">Search and select a new location</p>

				<section className="relative">
					<LocationSuggestions onPlaceSelect={handlePlaceSelect} />
				</section>

				<Tooltip.Provider>
					<Tooltip.Root>
						<Tooltip.Trigger asChild>
							<section className="group relative flex flex-col gap-3">
								<p
									data-is-disabled={
										isSliderDisabled ||
										!appliedFilters?.coordinates
									}
									className="text-xs font-semibold data-[is-disabled=true]:text-dm-strong-mid-grey"
								>
									Radius (in miles)
								</p>

								<div className="w-full">
									<RangeSlider
										min={0}
										max={40}
										defaultValue={[
											appliedFilters?.minDistance || 0,
											Math.min(
												appliedFilters?.maxDistance ||
													0,
												40
											)
										]}
										onValueCommit={handleRadiusChange}
										disabled={
											isSliderDisabled ||
											!appliedFilters?.coordinates
										}
									/>
								</div>

								{appliedFilters?.coordinates && (
									<div
										data-is-inactive={
											appliedFilters?.coordinates &&
											(appliedFilters?.state ||
												appliedFilters?.country)
										}
										className="hidden justify-center opacity-100 transition-opacity duration-700 ease-in-out data-[is-inactive=true]:opacity-0 lg:flex"
									>
										<Button
											onClick={handleShowOnMap}
											variant="secondary"
											size="small"
											disabled={Boolean(
												isSliderDisabled ||
													!appliedFilters?.coordinates ||
													appliedFilters?.state ||
													appliedFilters?.country
											)}
											className="gap-1 rounded-full border border-dm-gold text-xs font-normal text-dm-gold disabled:border-dm-strong-mid-grey disabled:text-dm-strong-mid-grey"
										>
											Show on map <Map />
										</Button>
									</div>
								)}
							</section>
						</Tooltip.Trigger>

						<Tooltip.Content
							className="max-w-52"
							align="start"
							side="bottom"
						>
							Radius slider and map button are disabled when a
							state/country is chosen
						</Tooltip.Content>
					</Tooltip.Root>
				</Tooltip.Provider>
			</FilterComponents.Content>
		</FilterComponents.Wrapper>
	);
};

type LocationSuggestionsProps = {
	onPlaceSelect: (
		place: Pick<GetGooglePlacesResponse, 'formatted_address' | 'geometry'>
	) => void;
};

const LocationSuggestions = ({ onPlaceSelect }: LocationSuggestionsProps) => {
	const { isOpen } = useFilters();

	const [searchBarValue, setSearchBarValue] = useState('');
	const [isPopoverOpen, setIsPopoverOpen] = useState(false);

	const { mutate, data, isLoading, isError } = useMutateLocationSuggestions();

	const locationSuggested = data?.data.data || [];

	const debouncedFetchSuggestions = useMemo(
		() => debounce(mutate, 300),
		[mutate]
	);

	const handleUpdateUserInput = (e: ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;

		setSearchBarValue(value);
		debouncedFetchSuggestions(value);

		if (value.length >= 2) {
			setIsPopoverOpen(true);
		}
	};

	const handleSetInitialState = () => {
		setSearchBarValue('');
		setIsPopoverOpen(false);
	};

	const handleSelectPlaceSuggestion = ({
		geometry,
		formatted_address
	}: GetGooglePlacesResponse) => {
		onPlaceSelect({ geometry, formatted_address });

		handleSetInitialState();
	};

	useEffect(() => {
		return () => {
			debouncedFetchSuggestions.cancel();
		};
	}, [debouncedFetchSuggestions]);

	useEffect(() => {
		if (!isOpen) {
			handleSetInitialState();
		}
	}, [isOpen]);

	return (
		<Popover.Root open={isOpen && isPopoverOpen}>
			<Popover.Anchor>
				<MagnifyingGlassGold className="absolute right-4 top-1/2 z-20 -translate-y-1/2" />

				<Input
					type="text"
					size="sm"
					placeholder="Enter target market address"
					value={searchBarValue}
					onChange={handleUpdateUserInput}
				/>
			</Popover.Anchor>

			<Popover.Content
				onOpenAutoFocus={e => e.preventDefault()}
				className="max-h-40 max-w-56 overflow-hidden overflow-y-auto border-dm-gray bg-dm-black3 p-0"
			>
				<ul className="flex h-full w-full flex-col">
					<li className="flex items-end justify-between border-b border-dm-gray bg-dm-black4 px-2 py-1.5 text-xs text-dm-strong-mid-grey">
						Suggested Locations
						<GoogleName className="h-auto w-10" />
					</li>

					<SuggestionsList
						isError={isError}
						isLoading={isLoading}
						placeList={locationSuggested}
						onSelectPlaceSuggestion={handleSelectPlaceSuggestion}
					/>
				</ul>
			</Popover.Content>
		</Popover.Root>
	);
};

type SuggestionsListProps = {
	isError: boolean;
	isLoading: boolean;
	placeList: GetGooglePlacesResponse[];
	onSelectPlaceSuggestion: (place: GetGooglePlacesResponse) => void;
};

const SuggestionsList = ({
	isError,
	isLoading,
	placeList,
	onSelectPlaceSuggestion
}: SuggestionsListProps) => {
	if (isError) {
		return (
			<li className="text-red-400 flex w-full items-center justify-center px-2 py-1.5 text-xs">
				Oops! We&apos;re unable to pull locations right now. Please try
				again.
			</li>
		);
	}

	if (!isLoading && placeList.length === 0) {
		return (
			<>
				<li className="border-b border-dm-gray px-2 py-1.5 text-sm text-dm-strong-mid-grey">
					No results found
				</li>

				<li className="flex flex-col items-start bg-dm-black4 px-2 py-1.5 text-xs text-dm-gold">
					<p>Is your location not showing up?</p>

					<p className="inline-flex gap-[1ch]">
						Contact
						<a href="mailto:support@re2.ai" className="font-bold">
							support@re2.ai
						</a>
					</p>
				</li>
			</>
		);
	}

	return (
		<>
			{isLoading ? (
				<li className="flex w-full items-center justify-center px-2 py-1.5">
					<Loader />
				</li>
			) : (
				placeList.map(place => (
					<li
						key={place.place_id}
						className="flex w-full items-center border-b border-dm-gray px-2 py-1.5"
					>
						<button
							type="button"
							className="w-full text-start"
							onClick={() => onSelectPlaceSuggestion(place)}
						>
							{place.formatted_address}
						</button>
					</li>
				))
			)}
		</>
	);
};
